import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback, useEffect } from 'react';
import { registerContextCleanupHandler, unregisterContextCleanupHandler } from './UserContext';

interface UserInterfaceState {
  dataDrawerMode: 'hidden' | 'compact' | 'fullscreen';
  isSidePanelOpen: boolean;
  isMediaDrawerVisible: boolean;
  shouldClosePopups: boolean; // Flag to trigger popup closure
  shouldZoomToHome: boolean; // Flag to trigger zoom to home/initial view
  endpointTimelineState: Record<string, boolean>; // Track timeline state per endpoint
  timelineHeight: string; // Configurable timeline height
  mediaDrawerManuallyClosedByUser: boolean; // Track if user manually closed MediaDrawer
  dataDrawerManuallyClosedOnMapTab: boolean; // Track if user manually closed DataDrawer while on Map tab
  // Tables view collapsible panels
  isDataGridCollapsed: boolean;
  // User preference for showing related items vs endpoint items
  userPreferredView: 'endpoint' | 'related' | 'auto';
}

interface UserInterfaceContextType {
  state: UserInterfaceState;
  setDataDrawerMode: (mode: 'hidden' | 'compact' | 'fullscreen') => void;
  setSidePanelOpen: (open: boolean) => void;
  setMediaDrawerVisible: (visible: boolean) => void;
  setMediaDrawerManuallyClosedByUser: (closed: boolean) => void;
  setDataDrawerManuallyClosedOnMapTab: (closed: boolean) => void;
  
  // DataDrawer control methods (moved from DataDrawerContext)
  openDataDrawerCompact: () => void;
  openDataDrawerFullscreen: () => void;
  closeDataDrawer: () => void;
  toggleDataDrawer: (currentTab?: number) => void;
  
  // Elegant endpoint-scoped timeline methods
  isTimelineVisibleForEndpoint: (endpointName: string) => boolean;
  setTimelineForEndpoint: (endpointName: string, visible: boolean) => void;
  toggleTimelineForEndpoint: (endpointName: string) => void;
  getCurrentTimelineVisibility: (currentEndpoint: { name: string } | null) => boolean;
  setTimelineHeight: (height: string) => void;
  
  // Tables view collapsible panel controls
  toggleDataGridCollapse: () => void;
  
  // User view preference controls
  setUserPreferredView: (view: 'endpoint' | 'related' | 'auto') => void;
  
  // Computed properties for layout coordination
  getDataDrawerHeight: (currentEndpoint?: { name: string } | null) => string;
  getDataDrawerTop: (currentEndpoint?: { name: string } | null) => string;
  getMediaDrawerBottom: () => number;
  
  // Popup management
  triggerClosePopups: () => void;
  resetClosePopupsFlag: () => void;
  
  // Map zoom management
  triggerZoomToHome: () => void;
  resetZoomToHomeFlag: () => void;
  
  // Session cleanup method for logout
  clearAllSessionState: () => void;
}

const UserInterfaceContext = createContext<UserInterfaceContextType | undefined>(undefined);

interface UserInterfaceProviderProps {
  children: ReactNode;
}

export const UserInterfaceProvider: React.FC<UserInterfaceProviderProps> = ({ children }) => {
  const [state, setState] = useState<UserInterfaceState>({
    dataDrawerMode: 'hidden',
    isSidePanelOpen: false, // Default to closed
    isMediaDrawerVisible: true,
    shouldClosePopups: false,
    shouldZoomToHome: false,
    endpointTimelineState: {}, // No endpoints have timeline enabled initially
    timelineHeight: '200px', // Single source of truth for timeline height
    mediaDrawerManuallyClosedByUser: false, // Default to not manually closed
    dataDrawerManuallyClosedOnMapTab: false, // Default to not manually closed on Map tab
    // Tables view collapsible panels
    isDataGridCollapsed: false,
    // Default to auto-switching behavior
    userPreferredView: 'auto',
  });

  const setDataDrawerMode = useCallback((mode: 'hidden' | 'compact' | 'fullscreen') => {
    setState(prev => ({ ...prev, dataDrawerMode: mode }));
  }, []);

  const setSidePanelOpen = useCallback((open: boolean) => {
    setState(prev => ({ ...prev, isSidePanelOpen: open }));
  }, []);

  const setMediaDrawerVisible = useCallback((visible: boolean) => {
    setState(prev => ({ ...prev, isMediaDrawerVisible: visible }));
  }, []);

  const setMediaDrawerManuallyClosedByUser = useCallback((closed: boolean) => {
    setState(prev => ({ ...prev, mediaDrawerManuallyClosedByUser: closed }));
  }, []);

  const setDataDrawerManuallyClosedOnMapTab = useCallback((closed: boolean) => {
    setState(prev => ({ ...prev, dataDrawerManuallyClosedOnMapTab: closed }));
  }, []);

  // Timeline control methods
  // Elegant endpoint-scoped timeline methods
  const isTimelineVisibleForEndpoint = useCallback((endpointName: string): boolean => {
    return state.endpointTimelineState[endpointName] ?? false;
  }, [state.endpointTimelineState]);

  const setTimelineForEndpoint = useCallback((endpointName: string, visible: boolean) => {
    // Timeline state updated for endpoint
    setState(prev => ({
      ...prev,
      endpointTimelineState: {
        ...prev.endpointTimelineState,
        [endpointName]: visible
      }
    }));
  }, []);

  const toggleTimelineForEndpoint = useCallback((endpointName: string) => {
    const currentState = state.endpointTimelineState[endpointName] ?? false;
    const newState = !currentState;
    // Timeline state toggled for endpoint
    setTimelineForEndpoint(endpointName, newState);
  }, [state.endpointTimelineState, setTimelineForEndpoint]);

  const getCurrentTimelineVisibility = useCallback((currentEndpoint: { name: string } | null): boolean => {
    if (!currentEndpoint) return false;
    return isTimelineVisibleForEndpoint(currentEndpoint.name);
  }, [isTimelineVisibleForEndpoint]);

  const setTimelineHeight = useCallback((height: string) => {
    setState(prev => ({ ...prev, timelineHeight: height }));
  }, []);

  // Tables view collapsible panel toggle methods
  const toggleDataGridCollapse = useCallback(() => {
    setState(prev => ({ ...prev, isDataGridCollapsed: !prev.isDataGridCollapsed }));
  }, []);

  // User view preference methods
  const setUserPreferredView = useCallback((view: 'endpoint' | 'related' | 'auto') => {
    setState(prev => ({ ...prev, userPreferredView: view }));
  }, []);

  // DataDrawer control methods
  const openDataDrawerCompact = useCallback(() => {
    setState(prev => {
      const wasHidden = prev.dataDrawerMode === 'hidden';
      return { 
        ...prev, 
        dataDrawerMode: 'compact',
        shouldClosePopups: wasHidden // Close popups when opening from hidden state
      };
    });
  }, []);

  const openDataDrawerFullscreen = useCallback(() => {
    setState(prev => {
      const wasHidden = prev.dataDrawerMode === 'hidden';
      return { 
        ...prev, 
        dataDrawerMode: 'fullscreen',
        shouldClosePopups: wasHidden // Close popups when opening from hidden state
      };
    });
  }, []);

  const closeDataDrawer = useCallback(() => {
    setState(prev => ({
      ...prev,
      dataDrawerMode: 'hidden',
      shouldClosePopups: false // Reset popup closure flag when closing
    }));
  }, []);

  const toggleDataDrawer = useCallback((currentTab?: number) => {
    setState(prev => {
      const currentMode = prev.dataDrawerMode;
      const newMode = currentMode === 'hidden' ? 'compact' : 'hidden';
      const isOpening = currentMode === 'hidden';
      const isClosingOnMapTab = !isOpening && currentTab === 1; // Map tab is index 1
      
      return { 
        ...prev, 
        dataDrawerMode: newMode,
        shouldClosePopups: isOpening, // Only close popups when opening, not when closing
        dataDrawerManuallyClosedOnMapTab: isClosingOnMapTab ? true : prev.dataDrawerManuallyClosedOnMapTab
      };
    });
  }, []);

  // Enhanced height calculation with endpoint-scoped timeline awareness
  const getDataDrawerHeight = useCallback((currentEndpoint?: { name: string } | null): string => {
    const titleBarHeight = 66;
    const isTimelineVisible = currentEndpoint ? getCurrentTimelineVisibility(currentEndpoint) : false;
    const timelineOffset = isTimelineVisible ? state.timelineHeight : '0px';
    
    if (state.dataDrawerMode === 'fullscreen') {
      return isTimelineVisible 
        ? `calc(100vh - ${titleBarHeight}px - ${timelineOffset})` 
        : "calc(100vh - 66px)";
    }
    
    return "calc(100vh - 50px)";
  }, [state.dataDrawerMode, state.timelineHeight, getCurrentTimelineVisibility]);
  
  const getDataDrawerTop = useCallback((currentEndpoint?: { name: string } | null): string => {
    const titleBarHeight = 66;
    const isTimelineVisible = currentEndpoint ? getCurrentTimelineVisibility(currentEndpoint) : false;
    const timelineOffset = isTimelineVisible ? state.timelineHeight : '0px';
    
    return isTimelineVisible 
      ? `calc(${titleBarHeight}px + ${timelineOffset})`
      : `${titleBarHeight}px`;
  }, [state.timelineHeight, getCurrentTimelineVisibility]);

  // Memoize the bottom position calculation
  const getMediaDrawerBottom = useCallback((): number => {
    // MediaDrawer sits at bottom with consistent margin
    return 20;
  }, []);

  // Popup management methods
  const triggerClosePopups = useCallback(() => {
    setState(prev => ({ ...prev, shouldClosePopups: true }));
  }, []);

  const resetClosePopupsFlag = useCallback(() => {
    setState(prev => ({ ...prev, shouldClosePopups: false }));
  }, []);

  // Map zoom management methods
  const triggerZoomToHome = useCallback(() => {
    setState(prev => ({ ...prev, shouldZoomToHome: true }));
  }, []);

  const resetZoomToHomeFlag = useCallback(() => {
    setState(prev => ({ ...prev, shouldZoomToHome: false }));
  }, []);

  // Clear all session state for logout
  const clearAllSessionState = useCallback(() => {
    console.log('[UserInterfaceContext] Clearing all session state for logout');
    
    // Reset to initial state
    setState({
      dataDrawerMode: 'hidden',
      isSidePanelOpen: false, // Default to closed
      isMediaDrawerVisible: true,
      shouldClosePopups: false,
      shouldZoomToHome: false,
      endpointTimelineState: {},
      timelineHeight: '200px', // Consistent with initial state
      mediaDrawerManuallyClosedByUser: false,
      dataDrawerManuallyClosedOnMapTab: false,
      // Reset Tables view collapsible panels
      isDataGridCollapsed: false,
      // Reset user view preference
      userPreferredView: 'auto',
    });
    
    console.log('[UserInterfaceContext] Session state cleared successfully');
  }, []);

  // Register cleanup handler
  useEffect(() => {
    registerContextCleanupHandler(clearAllSessionState);
    return () => unregisterContextCleanupHandler(clearAllSessionState);
  }, [clearAllSessionState]);

  // Memoize the entire context value to prevent unnecessary re-renders
  const contextValue = useMemo<UserInterfaceContextType>(() => ({
    state,
    setDataDrawerMode,
    setSidePanelOpen,
    setMediaDrawerVisible,
    setMediaDrawerManuallyClosedByUser,
    setDataDrawerManuallyClosedOnMapTab,
    openDataDrawerCompact,
    openDataDrawerFullscreen,
    closeDataDrawer,
    toggleDataDrawer,
    isTimelineVisibleForEndpoint,
    setTimelineForEndpoint,
    toggleTimelineForEndpoint,
    getCurrentTimelineVisibility,
    setTimelineHeight,
    toggleDataGridCollapse,
    setUserPreferredView,
    getDataDrawerHeight,
    getDataDrawerTop,
    getMediaDrawerBottom,
    triggerClosePopups,
    resetClosePopupsFlag,
    triggerZoomToHome,
    resetZoomToHomeFlag,
    clearAllSessionState,
  }), [state, setDataDrawerMode, setSidePanelOpen, setMediaDrawerVisible, setMediaDrawerManuallyClosedByUser, setDataDrawerManuallyClosedOnMapTab, openDataDrawerCompact, openDataDrawerFullscreen, closeDataDrawer, toggleDataDrawer, isTimelineVisibleForEndpoint, setTimelineForEndpoint, toggleTimelineForEndpoint, getCurrentTimelineVisibility, setTimelineHeight, toggleDataGridCollapse, setUserPreferredView, getDataDrawerHeight, getDataDrawerTop, getMediaDrawerBottom, triggerClosePopups, resetClosePopupsFlag, triggerZoomToHome, resetZoomToHomeFlag, clearAllSessionState]);

  return (
    <UserInterfaceContext.Provider value={contextValue}>
      {children}
    </UserInterfaceContext.Provider>
  );
};

export const useUserInterface = (): UserInterfaceContextType => {
  const context = useContext(UserInterfaceContext);
  if (!context) {
    throw new Error('useUserInterface must be used within a UserInterfaceProvider');
  }
  return context;
}; 